import { useEffect, useState } from "react";
import { api, API_BASE, getDepartmentFilter } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CalendarCheck, Download, FileText, Loader2, Users2 } from "lucide-react";
import { todayISO } from "@/lib/dateFormat";

const money = (n) => `$${Number(n || 0).toFixed(2)}`;
const DEPS = { dep_es: "Español", dep_en: "Inglés" };

/* Colores por estado, con la misma lógica del motor de cobros:
   verde = asistió y consumió · rojo = faltó sin avisar · ámbar = justificada
   gris = la escuela no la cobra (cancelada por el profe / fuera de plan) */
const COLOR = {
  present: "bg-emerald-100 text-emerald-900",
  absent: "bg-rose-100 text-rose-900",
  unjustified: "bg-rose-100 text-rose-900",
  unjustified_consumed: "bg-rose-100 text-rose-900",
  justified: "bg-amber-100 text-amber-900",
  teacher_cancelled: "bg-slate-200 text-slate-500",
  not_counted: "bg-slate-200 text-slate-500",
  removed: "bg-slate-200 text-slate-500",
};

export default function AttendanceGrid() {
  const [mes, setMes] = useState(todayISO().slice(0, 7));
  const [data, setData] = useState(null);
  const [cargando, setCargando] = useState(true);
  const dep = getDepartmentFilter();
  const depNombre = DEPS[dep] || "Español e Inglés";

  useEffect(() => {
    setCargando(true);
    api.get("/reports/attendance-grid", { params: { month: mes } })
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setCargando(false));
  }, [mes]);

  const bajar = (ext) => {
    const url = `${API_BASE}/api/reports/attendance-grid.${ext}?month=${mes}${dep ? `&department_id=${dep}` : ""}`;
    window.open(url, "_blank");
  };

  const dias = data?.days || [];

  return (
    <div className="space-y-5" data-testid="attendance-grid-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <CalendarCheck className="w-6 h-6 text-brand" /> Asistencia del mes
            <span className="text-[11px] font-semibold uppercase tracking-wide bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full"
                  data-testid="attendance-grid-department">
              {depNombre}
            </span>
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Por profesor: primero sus grupos y después sus privadas. El número es la clase
            del ciclo en la que va cada estudiante.
          </p>
        </div>
        <div className="flex items-end gap-2">
          <div>
            <label className="text-xs font-medium text-slate-600">Mes</label>
            <Input type="month" value={mes} onChange={(e) => setMes(e.target.value)}
                   className="w-40" data-testid="attendance-grid-month" />
          </div>
          <Button variant="outline" onClick={() => bajar("xlsx")} data-testid="attendance-grid-xlsx">
            <Download className="w-4 h-4" /> Excel
          </Button>
          <Button variant="outline" onClick={() => bajar("pdf")} data-testid="attendance-grid-pdf">
            <FileText className="w-4 h-4" /> PDF
          </Button>
        </div>
      </div>

      {data?.totals && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="attendance-grid-totals">
          {[
            { k: "students", label: "Estudiantes", v: data.totals.students ?? 0, cls: "text-slate-900" },
            { k: "sessions", label: "Clases dadas", v: data.totals.sessions ?? 0, cls: "text-emerald-600" },
            { k: "billed", label: "Facturado", v: money(data.totals.billed), cls: "text-slate-900" },
            { k: "paid", label: "Pagado", v: money(data.totals.paid), cls: "text-emerald-600" },
            { k: "pending", label: "Pendiente", v: money(data.totals.pending), cls: "text-amber-600" },
          ].map((c) => (
            <div key={c.k} className="bg-white border border-slate-200 rounded-xl p-4">
              <div className="text-xs uppercase tracking-wide text-slate-500">{c.label}</div>
              <div className={`text-xl font-bold mt-1 ${c.cls}`}>{c.v}</div>
            </div>
          ))}
        </div>
      )}

      <div className="flex flex-wrap gap-3 text-xs text-slate-600">
        {[["bg-emerald-100", "Asistió"], ["bg-rose-100", "Faltó sin avisar"],
          ["bg-amber-100", "Ausencia justificada"], ["bg-slate-200", "No se cobra"]].map(([c, l]) => (
            <span key={l} className="flex items-center gap-1.5">
              <span className={`inline-block w-3.5 h-3.5 rounded ${c} border border-slate-300`} /> {l}
            </span>
        ))}
      </div>

      {cargando ? (
        <div className="py-16 text-center text-slate-400">
          <Loader2 className="h-5 w-5 animate-spin mx-auto" />
        </div>
      ) : !data?.blocks?.length ? (
        <div className="bg-white border border-slate-200 rounded-xl p-10 text-center text-sm text-slate-500"
             data-testid="attendance-grid-empty">
          No hay clases registradas en <b>{depNombre}</b> para este mes.
          <div className="text-xs text-slate-400 mt-1">
            Probá otro mes, o cambiá el departamento con el selector de arriba.
          </div>
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-xl overflow-auto max-h-[70vh]">
          <table className="text-xs border-collapse">
            <thead className="sticky top-0 z-20">
              <tr className="bg-brand text-white">
                <th className="sticky left-0 z-30 bg-brand text-left px-3 py-2 min-w-[190px]">Estudiante</th>
                <th className="px-2 py-2 min-w-[62px]">Plan</th>
                {dias.map((d) => (
                  <th key={d.date}
                      className={`px-1 py-2 w-7 font-semibold ${d.weekend ? "opacity-60" : ""}`}>
                    <div>{d.letter}</div>
                    <div className="font-normal">{d.day}</div>
                  </th>
                ))}
                <th className="px-2 py-2 min-w-[56px]">Quedan</th>
                <th className="px-2 py-2 min-w-[80px]">Facturado</th>
                <th className="px-2 py-2 min-w-[72px]">Pagado</th>
                <th className="px-2 py-2 min-w-[76px]">Pendiente</th>
              </tr>
            </thead>
            <tbody>
              {data.blocks.map((b) => (
                <>
                  <tr key={`${b.teacher_name}-${b.group_name}`} className="bg-slate-100">
                    <td colSpan={dias.length + 6}
                        className="sticky left-0 px-3 py-1.5 font-semibold text-slate-700 bg-slate-100"
                        data-testid={`attendance-grid-block-${b.group_name}`}>
                      <span className="flex items-center gap-1.5">
                        <Users2 className="w-3.5 h-3.5 text-slate-400" />
                        {b.teacher_name} · {b.group_name}
                        {b.is_private && (
                          <span className="text-[10px] uppercase tracking-wide bg-violet-100 text-violet-700 px-1.5 py-0.5 rounded">
                            privada
                          </span>
                        )}
                        {b.schedule && <span className="font-normal text-slate-400">{b.schedule}</span>}
                      </span>
                    </td>
                  </tr>
                  {b.rows.map((r) => (
                    <tr key={r.enrollment_id} className="border-t border-slate-100 hover:bg-slate-50"
                        data-testid={`attendance-grid-row-${r.student_id}`}>
                      <td className="sticky left-0 z-10 bg-white px-3 py-1.5 font-medium text-slate-800 whitespace-nowrap">
                        {r.student_name}
                        {b.is_private && r.schedule && (
                          <span className="ml-1.5 font-normal text-[10px] text-slate-400 uppercase">{r.schedule}</span>
                        )}
                        {r.suspended && <span className="ml-1 text-[10px] text-amber-600">(cobro en pausa)</span>}
                      </td>
                      <td className="px-2 text-center text-slate-600">{r.plan}</td>
                      {dias.map((d) => {
                        const c = r.cells[d.date];
                        return (
                          <td key={d.date}
                              title={c ? `${d.date} · ${c.status}` : ""}
                              className={`text-center border-l border-slate-100 ${
                                c ? COLOR[c.status] || "bg-slate-50" : d.weekend ? "bg-slate-50" : ""
                              }`}>
                            {c ? (c.n ?? (c.status === "justified" ? "x" : "·")) : ""}
                          </td>
                        );
                      })}
                      <td className="px-2 text-center font-semibold text-slate-700 border-l border-slate-200">
                        {r.remaining ?? "—"}
                      </td>
                      <td className="px-2 text-right text-slate-700">{money(r.billed)}</td>
                      <td className="px-2 text-right text-emerald-700">{money(r.paid)}</td>
                      <td className="px-2 text-right text-amber-700">{money(r.pending)}</td>
                    </tr>
                  ))}
                </>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
